import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EmailPasswordStep } from "./steps/EmailPasswordStep";
import { NameStep } from "./steps/NameStep";
import { OTPStep } from "./steps/OTPStep";
import { VerificationMethodSelector } from "./VerificationMethodSelector";
import { PhoneStep } from "./steps/PhoneStep";

const STEPS = ['email', 'verification-method', 'otp', 'name', 'phone'] as const;
type Step = typeof STEPS[number];

export interface MinimalSignupData {
  email: string;
  password: string;
  name: string;
  phone?: string;
  verificationType?: 'email' | 'sms' | 'whatsapp';
}

export const MinimalSignupFlow = () => {
  const [currentStep, setCurrentStep] = useState<Step>('email');
  const [signupData, setSignupData] = useState<Partial<MinimalSignupData>>({});

  const currentStepIndex = STEPS.indexOf(currentStep);
  const progress = ((currentStepIndex + 1) / STEPS.length) * 100;

  const handleNext = (data: Partial<MinimalSignupData>) => {
    const updatedData = { ...signupData, ...data };
    setSignupData(updatedData);

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex]);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(STEPS[prevIndex]);
    }
  };

  const canGoBack = currentStepIndex > 0 && currentStep !== 'otp';

  const renderStep = () => {
    switch (currentStep) {
      case 'email':
        return <EmailPasswordStep onNext={handleNext} />;
      case 'verification-method':
        return (
          <VerificationMethodSelector
            onSelectEmail={() => {
              setSignupData({ ...signupData, verificationType: 'email' });
              setCurrentStep('otp');
            }}
            onSelectSMS={() => {
              setSignupData({ ...signupData, verificationType: 'sms' });
              setCurrentStep('phone');
            }}
            onSelectWhatsApp={() => {
              setSignupData({ ...signupData, verificationType: 'whatsapp' });
              setCurrentStep('phone');
            }}
          />
        );
      case 'phone':
        return <PhoneStep onNext={(data) => {
          handleNext(data);
          setCurrentStep('otp');
        }} />;
      case 'otp':
        return (
          <OTPStep 
            onNext={handleNext} 
            identifier={signupData.verificationType === 'sms' || signupData.verificationType === 'whatsapp' ? (signupData.phone || '') : (signupData.email || '')} 
            verificationType={signupData.verificationType || 'email'}
          />
        );
      case 'name':
        return <NameStep onNext={handleNext} initialData={signupData} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header com progresso */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-xl border-b border-border/50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-3">
            {canGoBack && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleBack}
                  className="shrink-0 hover:bg-primary/10 transition-all duration-200"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </motion.div>
            )}
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-muted-foreground font-medium">
                  Passo {currentStepIndex + 1} de {STEPS.length}
                </span>
                <motion.span
                  key={progress}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-sm font-bold text-primary"
                >
                  {Math.round(progress)}%
                </motion.span>
              </div>
              <div className="h-2.5 bg-muted/50 rounded-full overflow-hidden shadow-inner">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary via-primary/90 to-primary rounded-full shadow-glow-primary"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ 
                    duration: 0.5, 
                    ease: [0.4, 0, 0.2, 1],
                    type: "spring",
                    stiffness: 100
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo do step */}
      <div className="flex-1 container mx-auto px-4 py-8 max-w-md">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20, filter: "blur(4px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, x: -20, filter: "blur(4px)" }}
            transition={{ 
              duration: 0.4,
              ease: [0.4, 0, 0.2, 1]
            }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer - only show on non-email steps */}
      {currentStep !== 'email' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="py-4 text-center text-xs text-muted-foreground"
        >
          <p>
            Ao continuar, você concorda com nossos{" "}
            <a href="/terms" className="text-primary hover:underline transition-colors">
              Termos de Uso
            </a>
            {" e "}
            <a href="/privacy" className="text-primary hover:underline transition-colors">
              Política de Privacidade
            </a>
          </p>
        </motion.div>
      )}
    </div>
  );
};
